import playerDisplayColors from "../../playerColors";
import { LogMessageData, PlayerColorInfo } from "src/components/types";
import { fixMalformattedMessage } from "./utility";
import "./GameLogMessage.css";
import React, { memo } from "react";
import { isModKeyDown } from "src/components/utils";

type GameLogMessageProps = {
  logMessageData: LogMessageData;
  playerColors?: PlayerColorInfo[];
  indent: number;
  selected: boolean;
  setLogDetail: Function;
  messageIndex: number;
  displayTimestamp: number | false;
  getImage: (componentName: string) => string | undefined;
  associatedEventNumber: number;
  isTurnHeader: boolean;
  currentTurnRef?: React.RefObject<HTMLDivElement>;
  moveToEvent: ((eventNumber: number) => void) | undefined;
  createDebugTable: (eventNumber: number) => void;
  tableStatus: string;
  globalAssets?: { [key: string]: any };
  isFutureTurn: boolean;
  isCurrentTurn: boolean;
};

export const GameLogMessage = memo(function GameLogMessage({
  logMessageData,
  playerColors,
  indent,
  selected,
  setLogDetail,
  messageIndex,
  displayTimestamp,
  getImage,
  associatedEventNumber,
  isTurnHeader,
  currentTurnRef,
  moveToEvent,
  createDebugTable,
  tableStatus,
  globalAssets,
  isFutureTurn,
  isCurrentTurn,
}: GameLogMessageProps) {
  if (!logMessageData.message) {
    return <></>;
  }

  const logMessageEventLookup = logMessageData.event as { [key: string]: any };
  const defaultFontSize = +(logMessageData.event.logStyle?.fontSize?.slice(0, -2) ?? 14);

  let newIcons: { [key: string]: { url?: string } } = {};

  for (let name in globalAssets) {
    newIcons[name] = { url: globalAssets[name].url };
  }

  function generatePlayerSpan(variableKey: string, replaceString: string) {
    let style: { [key: string]: string } = {};
    let user = playerColors?.find((p) => p.color == logMessageEventLookup[variableKey]);
    let userName = user?.userInfo?.username;

    style.color = playerDisplayColors[logMessageData.event.sourcePlayer ?? ""];

    if (userName) {
      replaceString = userName;
    }

    return (
      <span className={"log-arg"} style={style} key={"playerargspan"}>
        {replaceString}{" "}
      </span>
    );
  }

  function generateImage(replaceString: string, keyIndex: string) {
    let targetIcon = newIcons[replaceString];
    let iconStyle: { [key: string]: string } = {};
    iconStyle.backgroundImage = targetIcon.url ?? "";
    iconStyle.height = defaultFontSize * 1.3 + "px";
    iconStyle.display = "inline-block";
    iconStyle.backgroundSize = "cover";
    return <img src={targetIcon.url} className="log-icon" style={iconStyle} key={"imgspan" + keyIndex}></img>;
  }

  function getFormattedMessageEl(modifiedMessageText: string) {
    let innerElements = [];
    let workingString = fixMalformattedMessage(modifiedMessageText);

    let messageParts = workingString.split(" ");
    for (let messagePartIndex in messageParts) {
      let messagePart = messageParts[messagePartIndex];
      if (messagePart.indexOf("{") !== -1) {
        let variableKey = messagePart.substring(1, messagePart.length - 1);
        let target = logMessageEventLookup[variableKey];
        let replaceString = typeof target === "string" ? target : JSON.stringify(target);
        if (!replaceString) {
          replaceString = variableKey;
        }

        if (variableKey === "sourcePlayer" || variableKey === "targetPlayer") {
          let playerSpan = generatePlayerSpan(variableKey, replaceString);
          innerElements.push(playerSpan);
        } else if (newIcons[replaceString]) {
          let imageEl = generateImage(replaceString, messagePartIndex);
          innerElements.push(imageEl);
        } else {
          innerElements.push(
            <span className={"log-arg"} key={"argspan-" + messagePartIndex}>
              {replaceString}{" "}
            </span>,
          );
        }
      } else {
        innerElements.push(<span key={"nonargspan-" + messagePartIndex}>{messagePart} </span>);
      }
    }
    return <div className="log-message-text">{innerElements}</div>;
  }

  function makeLogDetail() {
    if (selected) {
      setLogDetail(-1);
    } else {
      setLogDetail(messageIndex);
    }
  }

  function getTimestampEl() {
    if (displayTimestamp) {
      let dateTimeString = new Date(logMessageData.event.timestamp ?? 0).toLocaleString();
      return <div className="message-time">{dateTimeString}</div>;
    }
    return <></>;
  }

  let overallMessageStyle = Object.assign({ fontSize: defaultFontSize + "px" }, logMessageData.event.logStyle);
  let modifiedMessageText = logMessageData.message;
  if (logMessageData.event.editMode) {
    overallMessageStyle.color = "white";
    overallMessageStyle.outline = "none";
    overallMessageStyle.backgroundColor = "#5279bc";
    overallMessageStyle.fontSize = "10px";
    modifiedMessageText += " (edit mode)";
  }

  if (indent) {
    overallMessageStyle.marginLeft = indent * 5 + "px";
  }

  if (tableStatus === "ended" && isFutureTurn) {
    overallMessageStyle.color = "gray";
    overallMessageStyle.backgroundColor = "transparent";
  }

  if (tableStatus === "ended") {
    overallMessageStyle.cursor = "pointer";
  }

  function logMessageOnClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.stopPropagation();
    event.preventDefault();
    if (localStorage.getItem("user-role")?.includes("admin") && isModKeyDown(event)) {
      createDebugTable(associatedEventNumber); // create a debug table before the event clicked
    } else {
      if (tableStatus === "ended" && moveToEvent) {
        moveToEvent(associatedEventNumber + 1); // move to after the event clicked
      }
    }
  }

  return (
    <>
      {isCurrentTurn && isTurnHeader && (
        <div className="log-turn-divider" ref={currentTurnRef}>
          <span className="log-turn-divider-circle"></span>
        </div>
      )}
      <div
        className="log-message"
        style={overallMessageStyle}
        onMouseEnter={makeLogDetail}
        onMouseLeave={() => setLogDetail(-1)}
        data-assoc-ev-num={associatedEventNumber}
        onClick={logMessageOnClick}
      >
        {getFormattedMessageEl(modifiedMessageText)}
        {getTimestampEl()}
      </div>
    </>
  );
});
